import React from 'react';
import Select from 'react-select';
import { sendEmailOtp, verifyEmailOtp } from '../../../../../../Actions/FormActions';

const EmergencyContact = ({ formData, setFormData, errors = [], setErrors, isLocked = false }) => {
  // Local OTP state per contact (email only — mobile is collected unverified)
  const [otpState, setOtpState] = React.useState([]);

  // Persist only verification flags in session storage to survive section switches
  const storageKey = React.useMemo(() => {
    const formId = localStorage.getItem('form_id') || 'new';
    return `ec_otp_state_${formId}`;
  }, []);

  const loadPersisted = React.useCallback(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }, [storageKey]);

  const persistVerified = React.useCallback((state) => {
    try {
      const compact = (state || []).map((s) => ({
        emailVerified: !!s?.emailVerified
      }));
      sessionStorage.setItem(storageKey, JSON.stringify(compact));
    } catch (e) {
      // ignore storage errors
    }
  }, [storageKey]);

  // Ensure emergency_contacts is initialized
  React.useEffect(() => {
    if (!formData.emergency_contacts || formData.emergency_contacts.length === 0) {
      setFormData({
        ...formData,
        emergency_contacts: [{ name: '', mobile: '', relationship: '', email: '' }]
      });
    }
  }, []);

  // Keep otpState in sync with contacts length
  React.useEffect(() => {
    const contacts = formData.emergency_contacts || [];
    setOtpState((prev) => {
      const next = [...prev];
      const persisted = loadPersisted();
      while (next.length < contacts.length) {
        const contactIndex = next.length;
        next.push({
          emailOtpSent: false,
          emailOtp: '',
          emailLoading: false,
          emailVerified: Boolean(persisted?.[contactIndex]?.emailVerified),
          emailError: ''
        });
      }
      if (next.length > contacts.length) {
        next.length = contacts.length;
      }
      persistVerified(next);
      return next;
    });
  }, [formData.emergency_contacts, loadPersisted, persistVerified]);
  const handleChange = (index, field, value) => {
    const nextValue = field === 'mobile'
      ? String(value || '').replace(/[^0-9]/g, '').slice(0, 10)
      : value;
    const updatedContacts = [...formData.emergency_contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: nextValue
    };
    setFormData({
      ...formData,
      emergency_contacts: updatedContacts
    });

    // Reset OTP state if user edits the corresponding field
    setOtpState((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      if (field === 'email') {
        next[index] = { ...next[index], emailOtpSent: false, emailOtp: '', emailVerified: false, emailError: '' };
      }
      persistVerified(next);
      return next;
    });

    // Clear validation error for this field if now valid
    if (setErrors) {
      const isNowValid = (() => {
        if (field === 'name') return Boolean(nextValue && String(nextValue).trim() !== '');
        if (field === 'relationship') return Boolean(nextValue);
        if (field === 'mobile') return /^[0-9]{10}$/.test(String(nextValue || '').trim());
        if (field === 'email') return Boolean(String(nextValue || '').trim());
        return Boolean(nextValue);
      })();

      if (isNowValid) {
        setErrors((prev) => {
          const next = { ...prev };
          const idxErrors = { ...(next[index] || {}) };
          delete idxErrors[field];
          if (Object.keys(idxErrors).length === 0) {
            delete next[index];
          } else {
            next[index] = idxErrors;
          }
          return next;
        });
      }
    }
  };

  const addContact = () => {
    if (formData.emergency_contacts?.length >= 2) {
      return;
    }
    setFormData({
      ...formData,
      emergency_contacts: [
        ...formData.emergency_contacts,
        { name: '', mobile: '', relationship: '', email: '' }
      ]
    });
  };

  const handleEditEmail = (index) => {
    if (isLocked) return;
    setOtpState((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = {
        ...next[index],
        emailVerified: false,
        emailOtpSent: false,
        emailOtp: '',
        emailError: ''
      };
      persistVerified(next);
      return next;
    });
  };

  const handleSendEmailOtp = async (index) => {
    const email = formData.emergency_contacts?.[index]?.email || '';
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      setOtpState((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], emailError: 'Enter a valid email address' };
        return next;
      });
      return;
    }
    setOtpState((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], emailLoading: true, emailError: '' };
      persistVerified(next);
      return next;
    });
    const res = await sendEmailOtp(email);
    setOtpState((prev) => {
      const next = [...prev];
      if (res && res.status === 200) {
        next[index] = { ...next[index], emailOtpSent: true, emailLoading: false, emailError: '' };
      } else {
        const message = res?.data?.message || 'Failed to send OTP';
        next[index] = { ...next[index], emailLoading: false, emailError: message };
      }
      persistVerified(next);
      return next;
    });
  };

  const handleVerifyEmailOtp = async (index) => {
    const email = formData.emergency_contacts?.[index]?.email || '';
    const otp = otpState?.[index]?.emailOtp || '';
    if (!otp) {
      setOtpState((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], emailError: 'Please enter the OTP' };
        return next;
      });
      return;
    }
    setOtpState((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], emailLoading: true, emailError: '' };
      persistVerified(next);
      return next;
    });
    const res = await verifyEmailOtp(email, otp);
    setOtpState((prev) => {
      const next = [...prev];
      if (res && res.status === 200) {
        next[index] = { ...next[index], emailVerified: true, emailLoading: false, emailError: '' };
      } else {
        const message = res?.data?.message || 'Invalid OTP';
        next[index] = { ...next[index], emailLoading: false, emailError: message };
      }
      persistVerified(next);
      return next;
    });
  };

  return (
    <div className="space-y-8">
      <p className="text-sm text-[#753ABF]">
        Provide the details of the person who is your emergency contact.
      </p>

      {formData.emergency_contacts?.map((contact, index) => (
        <div key={index} className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1 gap-12">
          <div>
            <label className="block text-sm font-bold text-black">
              Full Name<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={contact.name}
              id={`ec_${index}_name`}
              onChange={(e) => !isLocked && handleChange(index, 'name', e.target.value)}
              disabled={isLocked}
              className={`mt-1 block w-full md:w-1/2 rounded-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 ${
                errors[index]?.name ? 'border-red-500' : 'border border-[#753ABF]'
              } ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
              placeholder="Type name here"
            />
            {errors[index]?.name && (
              <p className="mt-1 text-sm text-red-500">{errors[index].name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-black">
              Relationship<span className="text-red-500">*</span>
            </label>
            <Select
              value={contact.relationship ? { value: contact.relationship, label: contact.relationship } : null}
              onChange={(selectedOption) => !isLocked && handleChange(index, 'relationship', selectedOption.value)}
              options={[
                { value: 'Child', label: 'Child' },
                { value: 'Parent', label: 'Parent' },
                { value: 'Spouse', label: 'Spouse' },
                { value: 'Friend', label: 'Friend' },
                { value: 'Other', label: 'Other' }
              ]}
              inputId={`ec_${index}_relationship`}
              className="mt-1 md:w-1/2"
              classNamePrefix="react-select"
              placeholder="Choose Relationship"
              isDisabled={isLocked}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: isLocked ? '#cccccc' : (errors[index]?.relationship ? '#ef4444' : '#753ABF'),
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  boxShadow: 'none',
                  backgroundColor: isLocked ? '#eeecec' : 'white',
                  '&:hover': {
                    borderColor: isLocked ? '#cccccc' : '#753ABF',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  },
                  '&:focus': {
                    borderColor: isLocked ? '#cccccc' : '#753ABF',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    boxShadow: isLocked ? 'none' : '0 0 0 1px #753ABF'
                  }
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#753ABF' : state.isFocused ? '#753ABF20' : 'transparent',
                  color: state.isSelected ? 'white' : '#753ABF',
                  '&:active': {
                    backgroundColor: '#753ABF40'
                  }
                }),
                input: (base) => ({
                  ...base,
                  color: isLocked ? '#666666' : '#753ABF'
                }),
                singleValue: (base) => ({
                  ...base,
                  color: isLocked ? '#666666' : '#753ABF'
                })
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black">
              Phone no.<span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-stretch rounded-md shadow-sm w-full md:w-1/2">
              <span className="flex items-center px-3 rounded-l-md border border-[#753ABF] bg-gray-50 text-gray-500 text-md font-medium">
                +91
              </span>
              <input
                type="tel"
                value={contact.mobile}
                id={`ec_${index}_mobile`}
                onChange={(e) => !isLocked && handleChange(index, 'mobile', e.target.value)}
                disabled={isLocked}
                className={`flex-1 block rounded-none rounded-r-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 text-md ${
                  errors[index]?.mobile ? 'border-red-500' : 'border border-[#753ABF]'
                } ${isLocked ? 'bg-gray-50 text-[#666666]' : ''}`}
                placeholder="Type here"
                maxLength={10}
                inputMode="numeric"
                autoComplete="tel"
              />
            </div>
            {errors[index]?.mobile && (
              <p className="mt-1 text-sm text-red-500">{errors[index].mobile}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">With Whatsapp</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-black  ">
              Email
            </label>
            <div className="mt-1 flex items-center w-full md:w-1/2">
              <input
                type="email"
                value={contact.email || ''}
                id={`ec_${index}_email`}
                onChange={(e) => !isLocked && !otpState[index]?.emailVerified && handleChange(index, 'email', e.target.value)}
                disabled={isLocked || otpState[index]?.emailVerified}
                className={`flex-1 block rounded-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 ${
                  errors[index]?.email ? 'border-red-500' : 'border border-[#753ABF]'
                } ${(isLocked || otpState[index]?.emailVerified) ? 'bg-[#eeecec] text-[#666666]' : ''}`}
                placeholder="Type email here"
              />
              {otpState[index]?.emailVerified && (
                <button
                  type="button"
                  onClick={() => handleEditEmail(index)}
                  disabled={isLocked}
                  className={`${isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'} ml-2 p-1 rounded`}
                  title="Edit email"
                >
                  <img src="/Assets/Images/editFieldIcon.svg" alt="Edit email" className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="mt-2 flex items-center gap-3 w-full md:w-1/2">
              {!otpState[index]?.emailOtpSent && !otpState[index]?.emailVerified && (
                <button
                  type="button"
                  onClick={() => { if (!isLocked) handleSendEmailOtp(index); }}
                  disabled={isLocked || otpState[index]?.emailLoading}
                  className={`px-4 py-2 rounded-md text-white ${isLocked ? 'bg-[#753ABF]/40 cursor-not-allowed' : 'bg-[#753ABF] hover:bg-[#753ABF]/90'}`}
                >
                  {otpState[index]?.emailLoading ? 'Sending...' : 'Verify Email'}
                </button>
              )}
              {otpState[index]?.emailOtpSent && !otpState[index]?.emailVerified && (
                <>
                  <input
                    type="text"
                    value={otpState[index]?.emailOtp || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                      setOtpState((prev) => {
                        const next = [...prev];
                        next[index] = { ...next[index], emailOtp: value };
                        return next;
                      });
                    }}
                    className="flex-1 block rounded-md border border-[#753ABF] px-4 py-2.5 text-md"
                    placeholder="Enter OTP"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => { if (!isLocked) handleVerifyEmailOtp(index); }}
                    disabled={isLocked || otpState[index]?.emailLoading}
                    className={`px-4 py-2 rounded-md text-white ${isLocked ? 'bg-[#753ABF]/40 cursor-not-allowed' : 'bg-[#753ABF] hover:bg-[#753ABF]/90'}`}
                  >
                    {otpState[index]?.emailLoading ? 'Verifying...' : 'Verify'}
                  </button>
                </>
              )}
              {otpState[index]?.emailVerified && (
                <span className="text-green-600 text-sm">Email verified</span>
              )}
            </div>
            {errors[index]?.email && (
              <p className="mt-1 text-sm text-red-500">{errors[index].email}</p>
            )}
            {otpState[index]?.emailError && (
              <p className="mt-1 text-sm text-red-500 w-full md:w-1/2">{otpState[index].emailError}</p>
            )}
          </div>
        </div>
      ))}

      {formData.emergency_contacts?.length < 2 && (
        <button
          type="button"
          onClick={() => { if (!isLocked) addContact(); }}
          disabled={isLocked}
          className="text-[#753ABF] hover:text-[#753ABF]/80 text-sm font-medium flex items-center"
        >
          + Add more
        </button>
      )}
    </div>
  );
};

export default EmergencyContact;
